import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import ShareButton from "@/components/feature/ShareButton";
import FavoriteButton from "@/components/feature/FavoriteButton";
import usePageMeta from "@/hooks/usePageMeta";
import { getCharacterById, getRecommendByWorkId, categoryMeta } from "@/lib/content";
import { characterList } from "@/mocks/characters";
import { FAV_CHARACTER_KEY } from "@/lib/favorites";
import type { CharacterItem } from "@/types/content";

export default function CharacterDetail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getCharacterById(id) : undefined;

  usePageMeta({
    title: item ? `${item.name} - 香蕉动漫` : "该角色不存在 - 香蕉动漫",
    description: item ? `${item.name}（《${item.work}》）角色介绍：${item.intro}` : undefined,
    keywords: item ? `${item.name},${item.work},动漫角色,香蕉动漫` : undefined,
  });

  if (!item) {
    return (
      <div className="w-full px-4 md:px-6 py-10">
        <EmptyState
          icon="ri-user-unfollow-line"
          title="该角色不存在或已移除"
          description="你访问的角色资料可能已被移除或链接有误，看看其他角色介绍吧。"
          linkTo="/category/character"
          linkLabel="浏览角色介绍"
        />
      </div>
    );
  }

  const work = getRecommendByWorkId(item.workId);
  const others = (characterList as CharacterItem[])
    .filter((c) => c.id !== item.id)
    .slice(0, 4);

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: categoryMeta.character.title, to: "/category/character" },
          { label: item.name },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-6 lg:gap-8">
        <div className="w-full max-w-[320px]">
          <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-200">
            <img
              src={item.image}
              alt={item.name}
              title={`${item.name} - 香蕉动漫`}
              className="w-full h-full object-cover object-top"
            />
            <span className="absolute top-3 left-3 rounded-full bg-accent-500/95 px-3 py-1 text-xs font-bold text-white">
              {item.gender}
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <FavoriteButton storageKey={FAV_CHARACTER_KEY} id={item.id} label="收藏角色" />
            <ShareButton title={`${item.name} - 香蕉动漫`} />
          </div>
          <p className="mt-2 text-xs text-foreground-400 leading-relaxed">
            收藏仅保存在当前设备与浏览器中，清除浏览器数据后会丢失。
          </p>
        </div>

        <div className="min-w-0">
          <h1 className="font-display font-black text-2xl md:text-3xl lg:text-4xl text-foreground-950 leading-snug">
            {item.name}
          </h1>

          <div className="mt-4 flex flex-wrap gap-3">
            <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-2.5">
              <p className="text-xs text-foreground-400">所属作品</p>
              <p className="mt-0.5 text-sm font-medium text-foreground-900">《{item.work}》</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-2.5">
              <p className="text-xs text-foreground-400">性别</p>
              <p className="mt-0.5 text-sm font-medium text-foreground-900">{item.gender}</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-2.5">
              <p className="text-xs text-foreground-400">配音演员</p>
              <p className="mt-0.5 text-sm font-medium text-foreground-900">{item.voiceActor}</p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {item.tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-accent-700"
              >
                {t}
              </span>
            ))}
          </div>

          <section className="mt-7">
            <h2 className="font-display font-bold text-lg text-foreground-950 mb-2 flex items-center gap-2">
              <i className="ri-quill-pen-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
              角色简介
            </h2>
            <p className="text-sm md:text-base leading-8 text-foreground-700">{item.intro}</p>
          </section>

          <section className="mt-6">
            <h2 className="font-display font-bold text-lg text-foreground-950 mb-2 flex items-center gap-2">
              <i className="ri-book-2-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
              角色背景
            </h2>
            <div className="space-y-3 mt-2">
              {item.background.map((paragraph, index) => (
                <p key={index} className="text-sm md:text-base leading-8 text-foreground-700">
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          {work && (
            <Link
              to={`/detail/recommend/${work.id}`}
              className="mt-7 flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="w-16 h-20 rounded-md overflow-hidden shrink-0">
                <img
                  src={work.cover}
                  alt={work.title}
                  title={`${work.title} - 香蕉动漫`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-accent-600 font-medium mb-1">查看相关动画</p>
                <p className="font-display font-bold text-base text-foreground-950">
                  《{work.title}》
                </p>
                <p className="mt-0.5 text-xs text-foreground-500 line-clamp-1">
                  {work.genres.join(" / ")} · {work.region} · {work.year}
                </p>
              </div>
              <i className="ri-arrow-right-line ml-auto text-foreground-400 w-6 h-6 flex items-center justify-center"></i>
            </Link>
          )}

          <Link
            to="/category/character"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            返回角色介绍列表
          </Link>
        </div>
      </div>

      <section className="mt-12">
        <h2 className="font-display font-black text-xl md:text-2xl text-foreground-950 mb-5 flex items-center gap-3">
          <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
          更多角色推荐
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {others.map((c) => (
            <Link
              key={c.id}
              to={`/detail/character/${c.id}`}
              className="group block rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="relative w-full aspect-[4/5] overflow-hidden">
                <img
                  src={c.image}
                  alt={c.name}
                  title={`${c.name} - 香蕉动漫`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-3">
                <h3 className="font-bold text-sm text-foreground-950 line-clamp-1 group-hover:text-accent-600 transition-colors">
                  {c.name}
                </h3>
                <p className="mt-0.5 text-xs text-foreground-500">《{c.work}》</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}